import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PropertyAd {
  id: string;
  property_id: string;
  campaign_type: 'featured' | 'sponsored' | 'premium';
  title: string | null;
  budget: number | null;
  spent: number | null;
  cost_per_click: number | null;
  start_date: string;
  end_date: string | null;
  impressions: number;
  clicks: number;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  property?: {
    id: string;
    title: string;
    address: string;
    city: string;
    price: number;
    images: string[] | null;
  };
}

export function usePropertyAds() {
  return useQuery({
    queryKey: ['property-ads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_ads')
        .select(`
          *,
          property:properties(id, title, address, city, price, images)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PropertyAd[];
    },
  });
}

export function useActivePropertyAds(campaignType?: string) {
  return useQuery({
    queryKey: ['active-property-ads', campaignType],
    staleTime: 1000 * 60 * 5, // Ads don't change often – cache 5 min
    gcTime: 1000 * 60 * 15,
    queryFn: async () => {
      let query = supabase
        .from('property_ads')
        .select('property_id, campaign_type, priority')
        .eq('is_active', true)
        .or('end_date.is.null,end_date.gt.now()');

      if (campaignType) {
        query = query.eq('campaign_type', campaignType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useCreatePropertyAd() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (ad: {
      property_id: string;
      campaign_type: 'featured' | 'sponsored' | 'premium';
      title?: string;
      budget?: number;
      cost_per_click?: number;
      start_date?: string;
      end_date?: string;
      priority?: number;
    }) => {
      const { data, error } = await supabase
        .from('property_ads')
        .insert({
          ...ad,
          created_by: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-ads'] });
      queryClient.invalidateQueries({ queryKey: ['active-property-ads'] });
    },
  });
}

export function useUpdatePropertyAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PropertyAd> & { id: string }) => {
      const { data, error } = await supabase
        .from('property_ads')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-ads'] });
      queryClient.invalidateQueries({ queryKey: ['active-property-ads'] });
    },
  });
}

export function useDeletePropertyAd() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('property_ads')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-ads'] });
      queryClient.invalidateQueries({ queryKey: ['active-property-ads'] });
    },
  });
}

export function useTrackAdImpression() {
  return useMutation({
    mutationFn: async (propertyId: string) => {
      // Use type assertion since types may not be regenerated yet
      const { error } = await (supabase.rpc as any)('increment_ad_impressions', {
        p_property_id: propertyId,
      });
      // Silently fail - tracking shouldn't break the UI
      if (error) console.error('Failed to track impression:', error);
    },
  });
}

export function useTrackAdClick() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyId: string) => {
      // Use type assertion since types may not be regenerated yet
      const { error } = await (supabase.rpc as any)('increment_ad_clicks', {
        p_property_id: propertyId,
      });
      if (error) console.error('Failed to track click:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-ads'] });
    },
  });
}
